<template>
    <div>
        <!-- The entire notification is wrapped by the notificationContextMenu, this is for it to hover and appear correctly -->
        <NotificationContextMenu
            :disableContextMenu="disableContextMenu"
            :open="menuOpenId == notification.id"
            @close="$emit('closeMenu')"
            @open="$emit('openMenu', notification.id)"
        >
            <template v-slot:notification>
                <component
                    :is="notification.data?.not_clickable ? 'div' : 'Link'"
                    :href="getNotificationLink(notification)"
                    :id="notification.id"
                    class="group"
                    @click="clickNotification"
                >
                    <div
                        :class="[
                            { 'rounded-button': rounded },
                            'flex group-hover:bg-gray-50 py-2 pl-2 pr-3 transition-colors duration-button-hover relative',
                        ]"
                    >
                        <div class="flex flex-grow items-center gap-x-3 pr-1">
                            <div class="size-16 flex-shrink-0">
                                <ModelImage
                                    :modelClass="notification.sender_type"
                                    :src="notification.sender.picture"
                                    :typeId="notification.sender.type_id"
                                    :fileUrl="notification.sender.file"
                                    :class="[
                                        notification.sender_type == 'user'
                                            ? 'rounded-full'
                                            : 'rounded',
                                        'border',
                                    ]"
                                    imageClasses="size-full"
                                />
                            </div>
                            <div
                                :class="[
                                    { 'text-text-muted': !unread },
                                    'text-sm',
                                ]"
                            >
                                <p
                                    class="text-base underline decoration-transparent hover:decoration-current underline-offset-4 decoration-2 group-hover:decoration-current transition-colors duration-300"
                                    v-text="notification.sender.name"
                                />
                                <template v-if="notification.data.texts">
                                    <span
                                        v-for="(text, i) in notification.data
                                            .texts"
                                        :key="i"
                                        :is="
                                            isSystemNotification(notification)
                                                ? 'p'
                                                : 'span'
                                        "
                                        v-html="
                                            upperCaseFirst(
                                                getTranslation(
                                                    notification,
                                                    text
                                                )
                                            )
                                        "
                                    ></span>
                                </template>
                                <span
                                    v-else
                                    :is="
                                        isSystemNotification(notification)
                                            ? 'p'
                                            : 'span'
                                    "
                                    v-html="
                                        upperCaseFirst(
                                            getTranslation(notification)
                                        )
                                    "
                                ></span>
                                <div>
                                    <RealtimeTimestamp
                                        v-if="showTimeStamp"
                                        :timestamp="notification.created_at"
                                        :useTimeStampForPastDates="false"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center">
                            <div
                                :class="[
                                    unread && canShowUnreadDot
                                        ? 'bg-blue-500'
                                        : 'bg-transparent',
                                    'w-2.5 h-2.5 rounded-full flex-shrink-0 transition-colors duration-300',
                                ]"
                            />
                        </div>
                    </div>
                </component>
            </template>

            <template v-slot:notification-actions>
                <slot name="actions-menu" />
            </template>
        </NotificationContextMenu>
    </div>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import RealtimeTimestamp from '@/Components/Utilities/RealtimeTimestamp.vue'
import NotificationContextMenu from '@/Components/Notifications/NotificationContextMenu.vue'
import { upperCaseFirst } from '@/Tools/utilities'
export default {
    emits: ['closeMenu', 'openMenu', 'notificationClicked'],
    components: {
        Link,
        ModelImage,
        RealtimeTimestamp,
        NotificationContextMenu,
    },

    props: {
        notification: {
            type: Object,
            required: true,
        },
        rounded: {
            type: Boolean,
            default: true,
        },
        showTimeStamp: {
            type: Boolean,
            default: true,
        },
        menuOpenId: {
            type: [Number, String, null],
            required: true,
        },
        disableContextMenu: {
            type: Boolean,
            default: false,
        },
        canShowUnreadDot: {
            type: Boolean,
            default: true,
        },
        disableClickBusEvent: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        unread() {
            return this.notification.read_at == null
        },
    },

    methods: {
        upperCaseFirst,

        getNotificationLink(notification) {
            if (notification.data?.not_clickable) {
                return null
            }

            if (notification.data?.url) {
                let url = new URL(notification.data.url)
                url.searchParams.append('notification', notification.id)
                return url.toString()
            }

            return notification.show_url
        },

        clickNotification() {
            if (
                !this.notification.data?.not_clickable &&
                !this.disableClickBusEvent
            ) {
                this.$bus.emit('notification-clicked', {
                    notification: this.notification,
                })
            } else if (this.disableClickBusEvent) {
                this.$emit('notificationClicked')
            }
        },

        /**
         * Will try to translate the notifications content
         * The translationString is the notifications body, the translationVariables are
         * substrings that should be inserted into the translationString
         *
         * The translationVariables structure is an object where the keys are the same as the strings
         * that should be replaced in the translationstring
         * The value of that key can be a string or an object
         * If its an object, then check the key of that object to determine what to do.
         * If it is either first_parameter or second_parameter then it should retreive an attribute from
         * the notifications relations named first/second_parameter.
         * The value of the key first/second_parameter is the attribute that is to be retrieved from the relation
         *
         * example:
         * translationString: "you have a new :type"
         * translationVariables: {
         * 		type: {first_parameter: 'name'}
         * }
         *
         * Then notification.first_parameter.name will then be insterted where :type is
         *
         */
        getTranslation(notification, text = null) {
            let translationString = text ? text : notification.data.text

            let translationVariables = { ...notification.data.text_params }

            let val
            Object.keys(translationVariables).forEach((key) => {
                val = translationVariables[key]
                if (typeof val === 'object' && val != null) {
                    if (val.hasOwnProperty('translate')) {
                        val = this.$lang.get(val.translate)
                    } else if (
                        val.hasOwnProperty('translateSingularModelClass')
                    ) {
                        val = this.$getBaseModelTranslation({
                            modelName: val.translateSingularModelClass,
                            wordClass: 'singular',
                        })
                    } else if (
                        val.hasOwnProperty('translateDefinitiveModelClass')
                    ) {
                        val = this.$getBaseModelTranslation({
                            modelName: val.translateDefinitiveModelClass,
                            wordClass: 'definitive',
                        })
                    } else if (
                        val.hasOwnProperty('first_parameter') &&
                        notification.first_parameter != null
                    ) {
                        val = notification.first_parameter[val.first_parameter]
                    } else if (
                        val.hasOwnProperty('second_parameter') &&
                        notification.second_parameter != null
                    ) {
                        val =
                            notification.second_parameter[val.second_parameter]
                    } else if (val.hasOwnProperty('en')) {
                        val = val.hasOwnProperty(this.$lang.locale)
                            ? val[this.$lang.locale]
                            : val['en']
                    }

                    // Just a fallback if first_parameter or second_parameter is ued but for some reason the model is not retrieved
                    if (
                        (val.hasOwnProperty('first_parameter') &&
                            notification.first_parameter == null) ||
                        (val.hasOwnProperty('second_parameter') &&
                            notification.second_parameter == null)
                    ) {
                        translationVariables[key] = ''
                    }

                    if (
                        ['type', 'childtype', 'status'].includes(key) &&
                        val != null &&
                        typeof val == 'string'
                    ) {
                        val = val.toLowerCase()
                    }
                    if (key == 'icon') {
                        val = this.renderIcon(
                            val['icon'],
                            val['color'],
                            'icon-sm'
                        )
                    }
                    translationVariables[key] = val
                }
            })

            return this.$lang.get(translationString, translationVariables)
        },

        isSystemNotification(notification) {
            return (
                notification.sender_type == 'user' &&
                notification.sender_id == 1
            )
        },
    },
}
</script>
